import type { PreviewDiffTocChunk } from "@/apis/compare/route";
import type {
  CompareSidebarCategory,
  CompareSidebarSection,
} from "@/components/compare/compare-sidebar";
import { tocHasChanges } from "./preview-diff-changes";

// 백엔드 정렬/판별 규칙과 동일하게 맞춘다
// (xbrl-back .../ixd_preview_report_diff.py: _LEADING_NUMBER_RE, _CONSOLIDATED_MARKER).
const LEADING_NUMBER_RE = /^\s*\d+(?:-\d+)?/;
const CONSOLIDATED_MARKER = "(연결)";

const isNote = (displayLabel: string): boolean =>
  LEADING_NUMBER_RE.test(displayLabel);

export const isConsolidated = (displayLabel: string): boolean =>
  displayLabel.includes(CONSOLIDATED_MARKER);

export interface SidebarCategoryWithMeta extends CompareSidebarCategory {
  consolidated: boolean;
}

export interface SidebarData {
  categories: SidebarCategoryWithMeta[];
  showConsolidation: boolean;
}

/**
 * 누적 TOC 들로 CompareSidebar 카테고리/섹션을 파생한다(렌더 중 계산).
 *
 * 본문/주석으로 나누고, 연결·별도가 모두 존재할 때만 연결/별도 구분 정보를 포함한다.
 * showConsolidation=true 이면 호출 측에서 chip 으로 연결/별도를 필터링한다.
 */
export const buildSidebarCategories = (
  tocs: PreviewDiffTocChunk[],
): SidebarData => {
  if (tocs.length === 0) return { categories: [], showConsolidation: false };

  const showConsolidation =
    tocs.some((toc) => isConsolidated(toc.displayLabel)) &&
    tocs.some((toc) => !isConsolidated(toc.displayLabel));

  const byKey = new Map<
    string,
    { sections: CompareSidebarSection[]; note: boolean; consolidated: boolean }
  >();
  for (const toc of tocs) {
    const note = isNote(toc.displayLabel);
    const consolidated = isConsolidated(toc.displayLabel);
    const key = `${note ? "note" : "body"}-${consolidated ? "csl" : "sep"}`;
    const section: CompareSidebarSection = {
      id: toc.tocId,
      label: toc.displayLabel,
      hasChanges: tocHasChanges(toc),
    };
    const entry = byKey.get(key);
    if (entry) {
      entry.sections.push(section);
    } else {
      byKey.set(key, { sections: [section], note, consolidated });
    }
  }

  const categories: SidebarCategoryWithMeta[] = [...byKey.values()].map(
    ({ sections, note, consolidated }) => ({
      label: note ? "주석" : "본문",
      sections,
      consolidated,
    }),
  );

  return { categories, showConsolidation };
};
